import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Alert, Tooltip, CircularProgress, Popover, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lock as LockIcon,
  BookmarkAdd as MarkFocusIcon,
  BookmarkRemove as UnmarkFocusIcon,
} from '@mui/icons-material';
import { dailyFocusBlocksApi, calendarApi } from '../api/services';
import type { DailyFocusBlock, CalendarEvent } from '../types/models';
import { focusTimeCopy } from '../copy/focusTime';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am–7pm
// Each hour row is this tall in pixels. Events position themselves with
// absolute top/height computed from minutes-since-DAY_START_HOUR so a 30-min
// stand-up correctly draws at half a row, and an 85-min block draws to scale.
const ROW_HEIGHT = 36;
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 20;
const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;
const GRID_HEIGHT = (DAY_END_HOUR - DAY_START_HOUR) * ROW_HEIGHT;

// Focus block colours by source
const SOURCE_COLOURS: Record<string, string> = {
  AutoAllocated: 'rgba(108, 155, 255, 0.35)',
  Manual: 'rgba(129, 199, 132, 0.35)',
  DetectedFromCalendar: 'rgba(77, 208, 225, 0.35)',
};
const MEETING_COLOUR = 'rgba(255, 82, 82, 0.25)';
const CONFLICT_COLOUR = 'rgba(255, 82, 82, 0.5)';

function timeStrToHour(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display} ${ampm}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateOnly(d: Date): string {
  // Local-date components — using toISOString() would convert to UTC and shift
  // the day backwards in BST/CET, so a local Monday-midnight Date would key as
  // Sunday and the whole grid would draw one day out of phase.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatShortDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'AutoAllocated': return 'Auto';
    case 'Manual': return 'Manual';
    case 'DetectedFromCalendar': return 'Detected';
    default: return source;
  }
}

export default function FocusTimePage() {
  const [weekStart, setWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);
  const [gridEvents, setGridEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Add manual block dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addStart, setAddStart] = useState('09:00');
  const [addEnd, setAddEnd] = useState('11:00');
  const [addLabel, setAddLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Conflict resolution popover. Anchored on the conflict cell so the
  // Keep/Remove actions are attached directly to the moment the user clicks
  // the conflict rather than hidden in a menu bar.
  const [conflictAnchor, setConflictAnchor] = useState<HTMLElement | null>(null);
  const [conflictBlock, setConflictBlock] = useState<DailyFocusBlock | null>(null);
  const [conflictMeetingTitle, setConflictMeetingTitle] = useState<string>('');
  // Blocks the user has explicitly chosen to keep despite a conflict. Held in
  // component state only — the conflict returns if the page reloads, which is
  // the right behaviour: ack is for the current glance, not a persisted flag.
  const [acknowledgedConflicts, setAcknowledgedConflicts] = useState<Set<string>>(() => new Set());

  // Meeting action popover — anchored on a meeting block; offers
  // "Mark as focus" / "Unmark as focus" depending on whether the meeting is
  // already linked to a focus block.
  const [meetingAnchor, setMeetingAnchor] = useState<HTMLElement | null>(null);
  const [meetingTarget, setMeetingTarget] = useState<CalendarEvent | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // The 5 weekday dates for the current week
  const weekDates = DAYS.map((_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [blocks, events] = await Promise.all([
        dailyFocusBlocksApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
        calendarApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
      ]);
      setFocusBlocks(blocks);
      setGridEvents(events.filter(e => !e.isAllDay));
    } catch (err) {
      console.error('Failed to load focus data', err);
    } finally {
      setLoading(false);
    }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this focus block? Its calendar event will also be removed.')) return;
    try {
      await dailyFocusBlocksApi.delete(id);
      setFocusBlocks(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error('Failed to delete focus block', err);
    }
  };

  const openConflictPopover = (anchor: HTMLElement, block: DailyFocusBlock, meetingTitle: string) => {
    setConflictAnchor(anchor);
    setConflictBlock(block);
    setConflictMeetingTitle(meetingTitle);
  };
  const closeConflictPopover = () => {
    setConflictAnchor(null);
    setConflictBlock(null);
    setConflictMeetingTitle('');
  };
  const handleKeepFocus = () => {
    if (!conflictBlock) return;
    setAcknowledgedConflicts((prev) => {
      const next = new Set(prev);
      next.add(conflictBlock.id);
      return next;
    });
    closeConflictPopover();
  };
  const handleRemoveFocus = async () => {
    if (!conflictBlock) return;
    const id = conflictBlock.id;
    closeConflictPopover();
    try {
      await dailyFocusBlocksApi.delete(id);
      setFocusBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error('Failed to remove focus block', err);
    }
  };

  const openMeetingPopover = (anchor: HTMLElement, evt: CalendarEvent) => {
    setMeetingAnchor(anchor);
    setMeetingTarget(evt);
  };
  const closeMeetingPopover = () => {
    setMeetingAnchor(null);
    setMeetingTarget(null);
  };
  const handleMarkMeetingAsFocus = async () => {
    if (!meetingTarget) return;
    const evt = meetingTarget;
    closeMeetingPopover();
    try {
      const block = await dailyFocusBlocksApi.markMeetingAsFocus(evt.id);
      setFocusBlocks((prev) =>
        prev.some((b) => b.id === block.id) ? prev : [...prev, block]
      );
      setSnackbar(`Marked "${evt.title}" as focus time.`);
    } catch (err) {
      console.error('Failed to mark meeting as focus', err);
      setSnackbar('Could not mark meeting as focus. Please try again.');
    }
  };
  const handleUnmarkMeetingAsFocus = async () => {
    if (!meetingTarget) return;
    const evt = meetingTarget;
    closeMeetingPopover();
    try {
      await dailyFocusBlocksApi.unmarkMeetingAsFocus(evt.id);
      setFocusBlocks((prev) => prev.filter((b) => b.linkedCalendarEventId !== evt.id));
      setSnackbar(`Removed focus mark from "${evt.title}".`);
    } catch (err) {
      console.error('Failed to unmark meeting as focus', err);
      setSnackbar('Could not remove focus mark. Please try again.');
    }
  };

  const openAddDialog = (date?: string) => {
    setAddDate(date ?? formatDateOnly(weekDates[0]));
    setAddStart('09:00');
    setAddEnd('11:00');
    setAddLabel('');
    setAddError(null);
    setAddDialogOpen(true);
  };

  const handleSaveBlock = async () => {
    if (!addDate || !addStart || !addEnd) return;
    if (addEnd <= addStart) { setAddError('End time must be after start time.'); return; }
    setSaving(true);
    setAddError(null);
    try {
      const block = await dailyFocusBlocksApi.create({
        date: addDate,
        startTime: addStart,
        endTime: addEnd,
        label: addLabel || 'Focus Time',
      });
      setFocusBlocks(prev => [...prev, block]);
      setAddDialogOpen(false);
    } catch {
      setAddError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Build lookups for the grid
  // blocksByDateStr: date string → blocks for that day
  const blocksByDateStr: Record<string, DailyFocusBlock[]> = {};
  weekDates.forEach(d => { blocksByDateStr[formatDateOnly(d)] = []; });
  focusBlocks.forEach(b => {
    if (blocksByDateStr[b.date]) blocksByDateStr[b.date].push(b);
  });

  // meetingsByDateStr: date string → meetings for that day, keyed by local
  // calendar date (toLocaleDateString-style YYYY-MM-DD) so a meeting that
  // spans midnight in UTC still lands on the correct weekday cell.
  type GridMeeting = {
    id: string;
    startMinute: number; // minutes from DAY_START_HOUR
    endMinute: number;
    title: string;
    isMarkedAsFocus: boolean;
  };
  const meetingsByDateStr: Record<string, GridMeeting[]> = {};
  weekDates.forEach(d => { meetingsByDateStr[formatDateOnly(d)] = []; });

  // Set of calendar event ids the user has marked as focus, so the grid can
  // reflect "this meeting IS focus" without colour-clashing it as a conflict.
  const linkedEventIds = new Set(
    focusBlocks
      .map(b => b.linkedCalendarEventId)
      .filter((v): v is string => !!v)
  );

  gridEvents.forEach(evt => {
    const start = new Date(evt.startTime);
    const end = new Date(evt.endTime);
    const yyyy = start.getFullYear();
    const mm = String(start.getMonth() + 1).padStart(2, '0');
    const dd = String(start.getDate()).padStart(2, '0');
    const ds = `${yyyy}-${mm}-${dd}`;
    if (!meetingsByDateStr[ds]) return;
    const startMinute = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
    const endMinute = (end.getHours() - DAY_START_HOUR) * 60 + end.getMinutes();
    // Skip events entirely outside the visible day.
    if (endMinute <= 0 || startMinute >= TOTAL_MINUTES) return;
    meetingsByDateStr[ds].push({
      id: evt.id,
      startMinute: Math.max(0, startMinute),
      endMinute: Math.min(TOTAL_MINUTES, endMinute),
      title: evt.title,
      isMarkedAsFocus: linkedEventIds.has(evt.id),
    });
  });

  const minutesFromDayStart = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return (h - DAY_START_HOUR) * 60 + (m || 0);
  };
  const findEventById = (id: string): CalendarEvent | undefined =>
    gridEvents.find(e => e.id === id);

  // Summary stats
  const totalFocusMinutes = focusBlocks.reduce((sum, b) => {
    const s = timeStrToHour(b.startTime);
    const e = timeStrToHour(b.endTime);
    return sum + (e - s) * 60;
  }, 0);
  const totalFocusHours = Math.round(totalFocusMinutes / 60 * 10) / 10;
  const daysWithFocus = new Set(focusBlocks.map(b => b.date)).size;
  const autoBlocks = focusBlocks.filter(b => b.source === 'AutoAllocated').length;
  const manualBlocks = focusBlocks.filter(b => b.source === 'Manual').length;

  const isCurrentWeek = formatDateOnly(getMonday(new Date())) === formatDateOnly(weekStart);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4">Focus Time</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Week of {formatShortDate(weekStart)} to {formatShortDate(weekEnd)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => setWeekStart(d => addDays(d, -7))} size="small">
            <PrevIcon />
          </IconButton>
          <Button
            variant="text"
            size="small"
            disabled={isCurrentWeek}
            onClick={() => setWeekStart(getMonday(new Date()))}
          >
            This week
          </Button>
          <IconButton onClick={() => setWeekStart(d => addDays(d, 7))} size="small">
            <NextIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog()}>
            Add Block
          </Button>
        </Stack>
      </Box>

      {/* Summary cards */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Focus This Week</Typography>
            <Typography variant="h5">{totalFocusHours}h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Blocks</Typography>
            <Typography variant="h5">{focusBlocks.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Days Covered</Typography>
            <Typography variant="h5">{daysWithFocus} / 5</Typography>
          </Box>
          <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 3 }}>
            <Typography variant="caption" color="text.secondary">Auto-allocated</Typography>
            <Typography variant="h5">{autoBlocks}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Manual</Typography>
            <Typography variant="h5">{manualBlocks}</Typography>
          </Box>
          {totalFocusHours > 0 && (
            <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 3 }}>
              <Typography variant="caption" color="text.secondary">% of 40h week</Typography>
              <Typography variant="h5">{Math.round((totalFocusHours / 40) * 100)}%</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* First-run explainer. Shown only when this week has no blocks at
          all and we are not still loading. Teaches what focus time is,
          why the grid is empty, and how to start — per the §6 empty-state
          rule. */}
      {!loading && focusBlocks.length === 0 && (
        <Card
          sx={{
            mb: 3,
            bgcolor: 'rgba(108, 155, 255, 0.04)',
            border: 1,
            borderColor: 'rgba(108, 155, 255, 0.25)',
          }}
        >
          <CardContent sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            <ShieldIcon sx={{ color: 'primary.main', mt: 0.25 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {focusTimeCopy.firstRunHeading}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {focusTimeCopy.firstRunBody}
              </Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog()}>
                {focusTimeCopy.firstRunAction}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Shield info */}
      <Alert
        severity="info"
        icon={<ShieldIcon />}
        sx={{ mb: 3, bgcolor: 'rgba(108, 155, 255, 0.06)', border: '1px solid rgba(108, 155, 255, 0.15)' }}
      >
        <Typography variant="body2">
          <strong>Focus blocks protect your thinking time.</strong> The scheduling engine places work items
          in non-focus slots first. Focus blocks are only used as overflow when all other time is full.
          Run <strong>Start My Day</strong> to auto-allocate fresh blocks each morning.
        </Typography>
      </Alert>

      <Grid container spacing={2.5}>
        {/* Weekly Grid */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Weekly Focus Grid
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>

              {/* Legend */}
              <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SOURCE_COLOURS.AutoAllocated }} />
                  <Typography variant="caption" color="text.secondary">Auto-allocated</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SOURCE_COLOURS.Manual }} />
                  <Typography variant="caption" color="text.secondary">Manual</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SOURCE_COLOURS.DetectedFromCalendar }} />
                  <Typography variant="caption" color="text.secondary">Detected</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: MEETING_COLOUR }} />
                  <Typography variant="caption" color="text.secondary">Meeting</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: CONFLICT_COLOUR, border: 1, borderColor: 'error.main' }} />
                  <Typography variant="caption" color="text.secondary">Conflict</Typography>
                </Stack>
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '52px repeat(5, 1fr)', gap: 0, minWidth: 540 }}>
                  {/* Day headers */}
                  <Box />
                  {DAYS.map((day, i) => (
                    <Box key={day} sx={{ textAlign: 'center', py: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {day.substring(0, 3)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem' }}>
                        {formatShortDate(weekDates[i])}
                      </Typography>
                    </Box>
                  ))}

                  {/* Hour gutter — drawn once on the left, height matches the grid */}
                  <Box sx={{ position: 'relative', height: GRID_HEIGHT }}>
                    {HOURS.map((hour, idx) => (
                      <Box
                        key={hour}
                        sx={{
                          position: 'absolute',
                          top: idx * ROW_HEIGHT,
                          right: 6,
                          width: '100%',
                          textAlign: 'right',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {formatHour(hour)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* One absolutely-positioned column per weekday. Meetings and
                      focus blocks are drawn on top of the row guides at minute
                      precision — a 30-min event takes half a row, an 85-min
                      block draws to scale, and meetings inside a focus block
                      stay readable rather than being hidden by the focus tint. */}
                  {weekDates.map((date) => {
                    const ds = formatDateOnly(date);
                    const dayBlocks = blocksByDateStr[ds] || [];
                    const dayMeetings = meetingsByDateStr[ds] || [];
                    return (
                      <Box
                        key={ds}
                        sx={{
                          position: 'relative',
                          height: GRID_HEIGHT,
                          borderLeft: 1,
                          borderColor: 'divider',
                          background:
                            // 30-min subdivisions for visual rhythm
                            `repeating-linear-gradient(to bottom, transparent 0, transparent ${ROW_HEIGHT - 1}px, rgba(255,255,255,0.06) ${ROW_HEIGHT - 1}px, rgba(255,255,255,0.06) ${ROW_HEIGHT}px)`,
                        }}
                      >
                        {/* Focus blocks — drawn first so meetings layer above. */}
                        {dayBlocks.map(block => {
                          const startMin = minutesFromDayStart(block.startTime);
                          const endMin = minutesFromDayStart(block.endTime);
                          if (endMin <= 0 || startMin >= TOTAL_MINUTES) return null;
                          const top = (Math.max(0, startMin) / 60) * ROW_HEIGHT;
                          const height = ((Math.min(TOTAL_MINUTES, endMin) - Math.max(0, startMin)) / 60) * ROW_HEIGHT;
                          const overlappingMeeting = dayMeetings.find(
                            m => !m.isMarkedAsFocus && m.startMinute < endMin && m.endMinute > startMin
                          );
                          const isConflict =
                            !!overlappingMeeting && !acknowledgedConflicts.has(block.id);
                          const bg = isConflict
                            ? CONFLICT_COLOUR
                            : SOURCE_COLOURS[block.source] ?? SOURCE_COLOURS.AutoAllocated;
                          const tooltipText = isConflict
                            ? `Conflict: "${overlappingMeeting!.title}" overlaps with "${block.label}". Click to resolve.`
                            : `Focus block protected. ${block.label}`;
                          return (
                            <Tooltip key={block.id} title={tooltipText} placement="top" arrow>
                              <Box
                                role={isConflict ? 'button' : undefined}
                                tabIndex={isConflict ? 0 : undefined}
                                onClick={
                                  isConflict
                                    ? (e) => openConflictPopover(
                                        e.currentTarget as HTMLElement,
                                        block,
                                        overlappingMeeting!.title,
                                      )
                                    : undefined
                                }
                                onKeyDown={
                                  isConflict
                                    ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          openConflictPopover(
                                            e.currentTarget as HTMLElement,
                                            block,
                                            overlappingMeeting!.title,
                                          );
                                        }
                                      }
                                    : undefined
                                }
                                sx={{
                                  position: 'absolute',
                                  top,
                                  left: 2,
                                  right: 2,
                                  height,
                                  bgcolor: bg,
                                  border: 1,
                                  borderColor: isConflict ? 'error.main' : 'rgba(255,255,255,0.12)',
                                  borderRadius: 0.5,
                                  px: 0.5,
                                  py: 0.25,
                                  overflow: 'hidden',
                                  cursor: isConflict ? 'pointer' : 'default',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.25,
                                  zIndex: 1,
                                }}
                              >
                                {!isConflict && (
                                  <LockIcon
                                    sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.7, mt: 0.25 }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: isConflict ? 700 : 500,
                                    color: isConflict ? 'error.main' : 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                >
                                  {isConflict ? `⚠ ${overlappingMeeting!.title}` : block.label}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}

                        {/* Meetings — always drawn so the user can see what's
                            in the calendar even when a focus block sits at the
                            same time. Click opens the Mark/Unmark popover. */}
                        {dayMeetings.map(m => {
                          const top = (m.startMinute / 60) * ROW_HEIGHT;
                          const height = Math.max(14, ((m.endMinute - m.startMinute) / 60) * ROW_HEIGHT);
                          const meetingBg = m.isMarkedAsFocus
                            ? SOURCE_COLOURS.Manual
                            : MEETING_COLOUR;
                          const tooltip = m.isMarkedAsFocus
                            ? `${m.title} (marked as focus). Click to unmark.`
                            : `${m.title}. Click to mark as focus time.`;
                          return (
                            <Tooltip key={m.id} title={tooltip} placement="top" arrow>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  const evt = findEventById(m.id);
                                  if (evt) openMeetingPopover(e.currentTarget as HTMLElement, evt);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    const evt = findEventById(m.id);
                                    if (evt) openMeetingPopover(e.currentTarget as HTMLElement, evt);
                                  }
                                }}
                                sx={{
                                  position: 'absolute',
                                  top,
                                  left: 6,
                                  right: 6,
                                  height,
                                  bgcolor: meetingBg,
                                  border: 1,
                                  borderColor: m.isMarkedAsFocus ? 'success.main' : 'rgba(255,82,82,0.45)',
                                  borderLeftWidth: 3,
                                  borderRadius: 0.5,
                                  px: 0.5,
                                  py: 0.25,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  zIndex: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.25,
                                }}
                              >
                                {m.isMarkedAsFocus && (
                                  <ShieldIcon sx={{ fontSize: 12, color: 'success.main' }} />
                                )}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                >
                                  {m.title}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Blocks list */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                This Week's Blocks
              </Typography>

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!loading && focusBlocks.length === 0 && (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <AutoAwesomeIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    No focus blocks this week
                  </Typography>
                  <Typography color="text.disabled" variant="caption">
                    Run Start My Day to auto-allocate, or add one manually
                  </Typography>
                </Box>
              )}

              <Stack spacing={1} sx={{ mt: 1 }}>
                {focusBlocks
                  .slice()
                  .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
                  .map(block => {
                    const blockDate = new Date(block.date + 'T00:00:00');
                    const dayName = blockDate.toLocaleDateString('en-GB', { weekday: 'short' });
                    const dateStr = formatShortDate(blockDate);
                    const startH = block.startTime.substring(0, 5);
                    const endH = block.endTime.substring(0, 5);
                    const durationMins = Math.round(
                      (timeStrToHour(block.endTime) - timeStrToHour(block.startTime)) * 60
                    );

                    return (
                      <Box
                        key={block.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: 1,
                          borderColor: 'divider',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap fontWeight={500}>
                            {block.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayName} {dateStr} · {startH} to {endH} ({durationMins}m)
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            label={sourceLabel(block.source)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: SOURCE_COLOURS[block.source] ?? 'rgba(255,255,255,0.08)',
                              color: 'text.primary',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(block.id)}
                            sx={{ p: 0.25 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    );
                  })}
              </Stack>

              {focusBlocks.length > 0 && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openAddDialog()}
                  sx={{ mt: 2, width: '100%' }}
                  variant="outlined"
                >
                  Add manual block
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add manual block dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Focus Block</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              value={addDate}
              onChange={e => setAddDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start"
                type="time"
                value={addStart}
                onChange={e => setAddStart(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="time"
                value={addEnd}
                onChange={e => setAddEnd(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Label (optional)"
              value={addLabel}
              onChange={e => setAddLabel(e.target.value)}
              fullWidth
              size="small"
              placeholder="Focus Time"
            />
            {addError && <Alert severity="error">{addError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBlock} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict resolution popover. Anchored on the clicked cell; closes on
          blur or explicit action. Two options only ("Keep focus" or
          "Remove focus block") per the §5.3 rule: no third option. */}
      <Popover
        open={!!conflictAnchor}
        anchorEl={conflictAnchor}
        onClose={closeConflictPopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 320, borderRadius: 2 } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {focusTimeCopy.conflictTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {conflictMeetingTitle
            ? `"${conflictMeetingTitle}" overlaps this focus block.`
            : focusTimeCopy.conflictBody}
        </Typography>
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={<ShieldIcon />}
            onClick={handleKeepFocus}
          >
            {focusTimeCopy.conflictKeep}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveFocus}
          >
            {focusTimeCopy.conflictRemove}
          </Button>
        </Stack>
      </Popover>

      {/* Meeting action popover — Mark as focus / Unmark as focus. Anchored on
          the meeting block so the action attaches to the moment of intent. */}
      <Popover
        open={!!meetingAnchor}
        anchorEl={meetingAnchor}
        onClose={closeMeetingPopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 320, borderRadius: 2 } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {meetingTarget?.title || 'Meeting'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {linkedEventIds.has(meetingTarget?.id ?? '')
            ? 'This meeting is currently marked as focus time. Unmark to treat it as a normal meeting again.'
            : 'Mark this meeting as focus time so the scheduler treats it as protected and the planner counts it toward your focus hours.'}
        </Typography>
        <Stack spacing={1}>
          {linkedEventIds.has(meetingTarget?.id ?? '') ? (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<UnmarkFocusIcon />}
              onClick={handleUnmarkMeetingAsFocus}
            >
              Unmark as focus
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<MarkFocusIcon />}
              onClick={handleMarkMeetingAsFocus}
            >
              Mark as focus
            </Button>
          )}
        </Stack>
      </Popover>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
